import { BEDROCK_ANTHROPIC_FABLE_5_1_MODEL_REF } from "@arti/ai-models-config/bedrockAnthropic";
import { BEDROCK_CONVERSE_OPUS_5_5_MODEL_REF } from "@arti/ai-models-config/claudeOpus";
import { getContextWindowTokens } from "@arti/ai-models-config/contextWindows";
import {
  OPENAI_GPT_6_ASTRA_MODEL_REF,
  OPENAI_GPT_6_SOL_MODEL_REF,
} from "@arti/ai-models-config/openAi";
import { supportedReasoningEfforts } from "@arti/ai-models-config/reasoningEffortCapability";
import { SELECTABLE_ACTOR_MODELS } from "@arti/ai-models-config/selectableActorModels";

export type ModelProvider = "Anthropic" | "OpenAI";

export interface FeaturedModel {
  blurb: string;
  isNew: boolean;
  name: string;
  provider: ModelProvider;
  /** Must be an actor-selectable ref; the landing test enforces it. */
  ref: string;
  route: string;
}

export const FEATURED_MODELS: readonly FeaturedModel[] = [
  {
    blurb:
      "Anthropic's newest Opus. Adaptive thinking is always on, and it costs less than Opus 5.",
    isNew: true,
    name: "Claude Opus 5.5",
    provider: "Anthropic",
    ref: BEDROCK_CONVERSE_OPUS_5_5_MODEL_REF,
    route: "Amazon Bedrock",
  },
  {
    blurb:
      "Anthropic's Fable model, reachable through Amazon Bedrock or the Anthropic API.",
    isNew: false,
    name: "Claude Fable 5.1",
    provider: "Anthropic",
    ref: BEDROCK_ANTHROPIC_FABLE_5_1_MODEL_REF,
    route: "Bedrock or Anthropic",
  },
  {
    blurb: "OpenAI's GPT-6, with reasoning effort from low all the way to max.",
    isNew: false,
    name: "GPT-6 Astra",
    provider: "OpenAI",
    ref: OPENAI_GPT_6_ASTRA_MODEL_REF,
    route: "OpenAI API",
  },
  {
    blurb:
      "GPT-6 at a fifth of Astra's price, with reasoning from none up to max.",
    isNew: true,
    name: "GPT-6 Sol",
    provider: "OpenAI",
    ref: OPENAI_GPT_6_SOL_MODEL_REF,
    route: "OpenAI API",
  },
] as const;

/**
 * Display name for a selector label: drops the route suffix ("(Bedrock)") and
 * gives bare Claude tiers their family name, so routes of one model collapse.
 */
export function modelDisplayName(selectorLabel: string): string {
  const withoutRoute = selectorLabel.replace(/\s*\([^)]*\)$/, "");
  return /^(Haiku|Opus|Sonnet)\b/.test(withoutRoute)
    ? `Claude ${withoutRoute}`
    : withoutRoute;
}

/** Every distinct model an actor can run, in roster order. */
export const ACTOR_MODEL_NAMES: readonly string[] = [
  ...new Set(
    SELECTABLE_ACTOR_MODELS.map((model) => modelDisplayName(model.label)),
  ),
];

const FEATURED_NAMES = new Set(FEATURED_MODELS.map((model) => model.name));

export const OTHER_MODEL_NAMES: readonly string[] = ACTOR_MODEL_NAMES.filter(
  (name) => !FEATURED_NAMES.has(name),
);

export function formatContextWindow(modelRef: string): string | null {
  const tokens = getContextWindowTokens(modelRef);
  if (!tokens) {
    return null;
  }
  const millions = tokens / 1_000_000;
  return millions >= 1
    ? `${Number.parseFloat(millions.toFixed(2))}M tokens`
    : `${Math.round(tokens / 1000)}K tokens`;
}

export function formatEffortRange(modelRef: string): string | null {
  const efforts = supportedReasoningEfforts(modelRef);
  const first = efforts.at(0);
  const last = efforts.at(-1);
  if (!first || !last) {
    return null;
  }
  return first.value === last.value
    ? first.label
    : `${first.value} to ${last.value}`;
}
